import { Button, Typography } from '@lucid/lego';
import { Quote } from 'src/api/services/lupp/quotes/dto.quotes';
import { formatDate, formatString } from '../../../../../../../utils/tools';

/**
 * Format status string for display
 * @param status - Status value with dashes
 * @returns Formatted status string
 */
const formatStatus = (status: string): string => {
  return status
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

type CreateColumnsParams = {
  theme: 'light' | 'dark';
  onQuoteClick: (record: Quote) => void;
  onDownloadClick: (record: Quote) => void;
  labels: Record<string, string>;
  isDownloading?: boolean;
  downloadingQuoteId?: string | null;
};

/**
 * Create columns configuration for Quotes table
 * @param params - Column configuration parameters
 * @returns Array of column definitions
 */

export const formatCaseTitle = (caseId: string | null | undefined) => {
  if (!caseId) return 'xxxxxxxxxxxx';
  const shortId = caseId.slice(-12);
  return shortId.toUpperCase();
};

export const createColumns = ({
  theme,
  onQuoteClick,
  onDownloadClick,
  labels,
  isDownloading,
  downloadingQuoteId,
}: CreateColumnsParams) => [
  {
    key: 'quote_identifier',
    title: labels.quote_id || 'Quote ID',
    width: '140px',
    sortable: true,
    render: (_: unknown, record: Quote) => (
      <Typography
        variant="body"
        size="small"
        color="blue"
        theme={theme}
        onClick={() => onQuoteClick(record)}
      >
        {formatCaseTitle(record?.quote_identifier)}
      </Typography>
    ),
  },
  {
    key: 'model',
    title: labels.model_trim || 'Model/Trim',
    width: '180px',
    sortable: true,
    render: (_: unknown, record: Quote) => (
      <Typography variant="body" size="small" color="black" theme={theme}>
        {formatString(record.model)} {record.trim}
      </Typography>
    ),
  },
  {
    key: 'vin',
    title: labels.vin || 'VIN',
    width: '200px',
    sortable: true,
    render: (_: unknown, record: Quote) => (
      <Typography variant="body" size="small" color="black" theme={theme}>
        {record.vin}
      </Typography>
    ),
  },
  {
    key: 'vin_status',
    title: labels.vin_status || 'VIN Status',
    width: '140px',
    sortable: true,
    render: (_: unknown, record: Quote) => (
      <Typography variant="body" size="small" color="black" theme={theme}>
        {record.vin_status ? formatStatus(record.vin_status) : '-'}
      </Typography>
    ),
  },
  {
    key: 'total_amount',
    title: labels.total_amount || 'Total Amount',
    width: '120px',
    align: 'right' as const,
    sortable: true,
    render: (_: unknown, record: Quote) => (
      <Typography variant="body" size="small" color="black" theme={theme}>
        {record.total_amount ?? '-'}
      </Typography>
    ),
  },
  {
    key: 'payment_type',
    title: labels.payment_method || 'Payment Method',
    width: '140px',
    sortable: true,
    render: (_: unknown, record: Quote) => (
      <Typography variant="body" size="small" color="black" theme={theme}>
        {record.payment_type}
      </Typography>
    ),
  },
  {
    key: 'expiry_date',
    title: labels.expiry_date || 'Expiry Date',
    width: '140px',
    sortable: true,
    render: (_: unknown, record: Quote) => (
      <Typography variant="body" size="small" color="black" theme={theme}>
        {record?.expiry_date ? formatDate(record?.expiry_date) : 'N/A'}
      </Typography>
    ),
  },
  {
    key: 'status',
    title: labels.status || 'Status',
    width: '130px',
    sortable: true,
    render: (_: unknown, record: Quote) => (
      <Typography variant="body" size="small" color="black" theme={theme}>
        {record.status ? formatStatus(record.status) : '-'}
      </Typography>
    ),
  },
  {
    key: 'action',
    title: labels.action || 'Action',
    width: '120px',
    align: 'center' as const,
    sortable: false,
    render: (_: unknown, record: Quote) => {
      const isCurrentDownloading = isDownloading && downloadingQuoteId === record.quote_identifier;
      return (
        <Button
          variant="button"
          type="text"
          size="small"
          label={isCurrentDownloading ? 'Downloading...' : labels.download || 'Download'}
          handleClick={() => onDownloadClick(record)}
          gaComponent="quotes-download"
          dataTestId={`quote-download-${record.quote_identifier}`}
          theme={theme}
          disabled={isCurrentDownloading}
        />
      );
    },
  },
];
